/**
 * Pagination - Shared pagination footer for admin list pages.
 * Renders "Showing X to Y of Z {itemLabel}" on the left and
 * First / Previous / Page N of M / Next / Last buttons on the right.
 */

export interface PaginationProps {
  page: number;
  total: number;
  limit: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
}

const btnClass =
  'px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm';

export default function Pagination({
  page,
  total,
  limit,
  itemLabel,
  onPageChange,
}: PaginationProps) {
  const totalPages = Math.ceil(total / limit) || 1;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
      <p className="text-sm text-warm-600">
        Showing {from} to {to} of {total} {itemLabel}
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(1)}
          disabled={page === 1}
          className={btnClass}
        >
          First
        </button>
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className={btnClass}
        >
          Previous
        </button>
        <span className="text-sm text-warm-600 px-1">
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className={btnClass}
        >
          Next
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={page === totalPages}
          className={btnClass}
        >
          Last
        </button>
      </div>
    </div>
  );
}
